import { GetSubmissionResponsePortal } from "@rubi/api-generated";

export interface ICoordinates {
  lat: number;
  lng: number;
}

/** Format an ISO date string for display, or "N/A" when missing. */
export const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Best-effort extraction of a human-readable location from a submission's
 * survey answers. Scans for address/location/place-like keys and reads the
 * first usable string/description/formatted_address.
 */
export const extractLocation = (
  submission: GetSubmissionResponsePortal | null,
) => {
  if (!submission?.submission) return "N/A";
  const data = submission.submission as Record<string, any>;

  for (const key of Object.keys(data)) {
    const lowerKey = key.toLowerCase();
    if (
      lowerKey.includes("address") ||
      lowerKey.includes("location") ||
      lowerKey.includes("place")
    ) {
      const val = data[key];
      if (typeof val === "string" && val.trim()) return val;
      if (val?.googleMapsPlace?.formatted_address)
        return val.googleMapsPlace.formatted_address;
      if (val?.description) return val.description;
      if (val?.formatted_address) return val.formatted_address;
      if (Array.isArray(val) && val.length > 0) {
        const first = val[0];
        if (typeof first === "string") return first;
        if (first?.description) return first.description;
        if (first?.formatted_address) return first.formatted_address;
      }
    }
  }
  return "N/A";
};

/** Best-effort extraction of an incident/occurrence date from survey answers. */
export const extractDate = (submission: GetSubmissionResponsePortal | null) => {
  if (!submission?.submission) return null;
  const data = submission.submission as Record<string, any>;

  for (const key of Object.keys(data)) {
    const lowerKey = key.toLowerCase();
    if (
      lowerKey.includes("seizure") ||
      lowerKey.includes("execution") ||
      lowerKey.includes("incident_date") ||
      lowerKey.includes("occurrence_date") ||
      lowerKey.includes("date_of")
    ) {
      const val = data[key];
      if (typeof val === "string" && val.trim()) return val;
    }
  }
  return null;
};

/**
 * A coordinate is usable only if both components are finite, in range, and not
 * (0, 0). Address-autocomplete answers default geometry to `{ lat: 0, lng: 0 }`
 * when a place is saved without a resolved location (see transformPlaceObject
 * in online-reporting/utils.ts) — so (0, 0) means "no location", not the Gulf
 * of Guinea. Plotting those dumped every pin on Null Island off Africa.
 */
export const isValidCoordinate = (lat: number, lng: number): boolean =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  Math.abs(lat) <= 90 &&
  Math.abs(lng) <= 180 &&
  !(Math.abs(lat) < 1e-6 && Math.abs(lng) < 1e-6);

/** A stored googleMapsPlace value (geometry/place_id/formatted_address). */
export interface PlaceRef {
  placeId: string | null;
  address: string | null;
}

// Rank survey answer keys so an incident/location address wins over a personal
// or home address when a submission has several geocoded fields.
const keyScore = (key: string): number => {
  const k = key.toLowerCase();
  let score = 0;
  if (
    k.includes("incident") ||
    k.includes("seizure") ||
    k.includes("execution")
  )
    score += 3;
  if (k.includes("location") || k.includes("address") || k.includes("place"))
    score += 2;
  if (
    k.includes("home") ||
    k.includes("reporting") ||
    k.includes("party") ||
    k.includes("person") ||
    k.includes("owner")
  )
    score -= 3;
  return score;
};

/**
 * Choose the most relevant googleMapsPlace from a submission's answers,
 * preferring one with a valid geometry; failing that, the best-ranked place
 * that still carries a place_id/address we can geocode. Returns the chosen
 * place plus whether its geometry is directly usable.
 */
const pickBestPlace = (
  submission: GetSubmissionResponsePortal | null,
): { place: Record<string, any>; hasValidGeometry: boolean } | null => {
  if (!submission?.submission) return null;
  const data = submission.submission as Record<string, any>;

  const places: { score: number; place: Record<string, any> }[] = [];
  for (const key of Object.keys(data)) {
    const val = data[key];
    const candidates = Array.isArray(val) ? val : [val];
    for (const candidate of candidates) {
      const place = candidate?.googleMapsPlace;
      if (place && typeof place === "object") {
        places.push({ score: keyScore(key), place });
      }
    }
  }
  if (places.length === 0) return null;

  const hasValid = (p: Record<string, any>) =>
    isValidCoordinate(Number(p?.geometry?.lat), Number(p?.geometry?.lng));
  const best = (pool: typeof places) =>
    pool.reduce((a, b) => (b.score > a.score ? b : a)).place;

  const located = places.filter((p) => hasValid(p.place));
  if (located.length > 0) {
    return { place: best(located), hasValidGeometry: true };
  }
  const geocodable = places.filter(
    (p) => p.place?.place_id || p.place?.formatted_address,
  );
  if (geocodable.length > 0) {
    return { place: best(geocodable), hasValidGeometry: false };
  }
  return null;
};

/**
 * lat/lng for a submission, or null when it has no usable (valid) geometry.
 * Invalid/zero geometry is treated as "no location" rather than plotted.
 */
export const extractCoordinates = (
  submission: GetSubmissionResponsePortal | null,
): ICoordinates | null => {
  const best = pickBestPlace(submission);
  if (!best?.hasValidGeometry) return null;
  return {
    lat: Number(best.place.geometry.lat),
    lng: Number(best.place.geometry.lng),
  };
};

/**
 * A place_id/address to geocode when a submission's stored geometry is missing
 * or invalid, so the pin can still be placed correctly. Null when there is
 * nothing to geocode (or the geometry was already valid).
 */
export const extractPlaceRef = (
  submission: GetSubmissionResponsePortal | null,
): PlaceRef | null => {
  const best = pickBestPlace(submission);
  if (!best || best.hasValidGeometry) return null;
  const placeId =
    typeof best.place.place_id === "string" && best.place.place_id
      ? best.place.place_id
      : null;
  const address =
    typeof best.place.formatted_address === "string" &&
    best.place.formatted_address
      ? best.place.formatted_address
      : null;
  return placeId || address ? { placeId, address } : null;
};
